import { isDictationCustomVocabularyEnabled } from '@/features/flags';
import { getCurrentUserSnippets } from '@/hooks/query/useQueryUserSnippets';
import { type CustomVocabularyEntryInput } from '@heidi/dictate/contracts';
import { type UserResponse } from '@heidi/workspace/contracts';
import {
  readDefaultDictationInputLanguages,
  readDefaultDictationOutputLanguages,
} from '@heidi/workspace/data';
import { getCurrentCustomVocabularyEntries } from './customVocabularyCache';

export interface DictationSnippet {
  verbal_trigger: string;
  content: string;
}

export interface UserContext {
  date_format: string;
  spelling_preference: string;
  word_library: Record<string, string> | null;
  dictation_context?: string | null;
  /** @deprecated use `output_languages`; kept for the `release-dictation-language-settings` flag-off code path. */
  output_language_code?: string | null;
  output_languages?: string[] | null;
  input_languages: string[] | null;
  clinician_title: string;
  clinician_firstname: string;
  clinician_surname: string;
  // Omitted when empty so the backend default applies.
  snippets?: DictationSnippet[];
  // Each term with its misspellings. Omitted when the
  // `release-dictation-custom-vocabulary` flag is off or the list is empty so
  // the backend default applies.
  custom_vocabulary?: CustomVocabularyEntryInput[];
}

export function buildUserContext(user: UserResponse | null | undefined): UserContext {
  const settings = user?.session_settings;
  const inputLanguages = readDefaultDictationInputLanguages() ?? null;
  const { outputLanguages, outputLanguageCode } = readDefaultDictationOutputLanguages();

  // Verbal-trigger snippets from cache (warmed by DictationBubble).
  const snippets = (getCurrentUserSnippets()?.snippets ?? [])
    .filter((snippet) => snippet.is_verbal_trigger && !snippet.is_default)
    .map((snippet) => ({
      verbal_trigger: (snippet.trigger_phrase ?? '').trim(),
      content: snippet.content,
    }))
    .filter((snippet) => snippet.verbal_trigger.length > 0);

  const customVocabulary = isDictationCustomVocabularyEnabled()
    ? getCurrentCustomVocabularyEntries()
    : [];
  return {
    date_format: settings?.date_format ?? 'DD/MM/YYYY',
    // BE-resolved effective value; null/absent → '' (no session fallback)
    spelling_preference: user?.dictation?.spelling_preference ?? '',
    word_library: settings?.user_word_library ?? null,
    // Mutual exclusivity: only one of these keys is present per request.
    ...(outputLanguageCode !== null && { output_language_code: outputLanguageCode }),
    ...(outputLanguages !== null && { output_languages: outputLanguages }),
    input_languages: inputLanguages,
    clinician_title: user?.title ?? '',
    clinician_firstname: user?.first_name ?? '',
    clinician_surname: user?.last_name ?? '',
    ...(snippets.length > 0 && { snippets }),
    ...(customVocabulary.length > 0 && { custom_vocabulary: customVocabulary }),
  };
}
